import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useSubscription } from '../context/SubscriptionContext'
import { apiClient } from '../lib/apiClient'
import { auditService } from '../services/auditService'
import { subscriptionService, PLAN_LABELS, PLAN_PRICES, SubscriptionTier } from '../services/subscriptionService'
import { legalService, LegalAcceptance } from '../services/legalService'
import { OrgMember } from '../types'
import {
  BarChart3, CreditCard, Users, Settings, ClipboardList,
  FolderOpen, EditIcon, Trash2, Mail, Key, Activity, Shield, Building
} from '../components/icons/Icons'
import s from './AdminPanel.module.css'
import ObservabilityPage from './ObservabilityPage'
import { useTour } from '../context/TourContext'
import { TourOverlay } from '../components/ui/TourOverlay'
import TeamManagement from '../components/features/TeamManagement'
import DepartmentManagement from '../components/features/DepartmentManagement'
import SubscriptionManager from '../components/features/SubscriptionManager'
import BillingHistory from '../components/features/BillingHistory'
import { TrialExpirationModal } from '../components/features/TrialExpirationModal'
import ReleaseNotes from '../components/features/ReleaseNotes'
import { CURRENT_VERSION } from '../data/releaseNotes'

const BILLING_EMAIL = 'billing@resourceflow.com'

type AdminSection = 'overview' | 'subscription' | 'users' | 'departments' | 'settings' | 'audit' | 'legal' | 'observability' | 'releases'

interface AuditLog {
  id: string
  actor_display_name: string | null
  action: string
  entity_type: string
  entity_label: string | null
  created_at: string
}

function AuditActionIcon({ action }: { action: string }) {
  const size = 14
  const style: React.CSSProperties = { flexShrink: 0 }
  if (action.includes('created')) return <FolderOpen size={size} style={style} />
  if (action.includes('updated') || action.includes('settings')) return <EditIcon size={size} style={style} />
  if (action.includes('deleted') || action.includes('removed')) return <Trash2 size={size} style={style} />
  if (action.includes('invited')) return <Mail size={size} style={style} />
  if (action.includes('role_changed')) return <Key size={size} style={style} />
  if (action.includes('subscription')) return <CreditCard size={size} style={style} />
  return <Activity size={size} style={style} />
}

export default function AdminPanel() {
  const { organization, orgMember, user } = useAuth()
  const { subscription, limits, daysLeft } = useSubscription()
  const [section, setSection] = useState<AdminSection>('overview')
  const { registerAdminSectionSetter, startTour, isTourActive } = useTour()
  const { orgMember: authOrgMember, user: authUser } = useAuth()
  const [showTrialModal, setShowTrialModal] = useState(false)
  const [members, setMembers] = useState<OrgMember[]>([])
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [auditFilter, setAuditFilter] = useState('')
  const [auditEntityFilter, setAuditEntityFilter] = useState('')
  const [orgName, setOrgName] = useState(organization?.name || '')
  const [defaultCurrency, setDefaultCurrency] = useState(organization?.default_currency || 'USD')
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')
  const [usage, setUsage] = useState({ users: 0, projects: 0, resources: 0 })
  const [allLimits, setAllLimits] = useState<any[]>([])
  const [legalAcceptances, setLegalAcceptances] = useState<LegalAcceptance[]>([])

  const loadMembers = useCallback(async () => {
    if (!organization) return
    const data = await apiClient.get<OrgMember[]>(`/orgs/${organization.id}/members`)
    setMembers((data || []).map((m: any) => ({ ...m, email: m.email || '' })))
  }, [organization])

  const loadAuditLogs = useCallback(async () => {
    if (!organization) return
    try {
      const logs = await auditService.getLogs(organization.id)
      setAuditLogs(logs as AuditLog[])
    } catch { }
  }, [organization])

  const loadUsage = useCallback(async () => {
    if (!organization) return
    const data = await apiClient.get<{ users: number; projects: number; resources: number }>(
      `/orgs/${organization.id}/usage`
    )
    setUsage({ users: data?.users || 0, projects: data?.projects || 0, resources: data?.resources || 0 })
  }, [organization])

  useEffect(() => {
    loadMembers()
    loadUsage()
    subscriptionService.getAllLimits().then(setAllLimits)
  }, [loadMembers, loadUsage])

  useEffect(() => {
    registerAdminSectionSetter((s: string) => setSection(s as AdminSection))
  }, [registerAdminSectionSetter])

  useEffect(() => {
    const onboardingCompleted =
      (authUser as any)?.onboarding_completed === true ||
      localStorage.getItem('allocatiq_onboarding_completed') === 'true'
    if (!onboardingCompleted && !isTourActive) {
      const timer = setTimeout(() => startTour(), 800)
      return () => clearTimeout(timer)
    }
  }, [authUser, isTourActive, startTour])

  useEffect(() => {
    if (section === 'audit') loadAuditLogs()
    if (section === 'legal' && organization) {
      legalService.getOrgMemberAcceptances(organization.id).then(setLegalAcceptances)
    }
  }, [section, loadAuditLogs, organization])

  const handleRoleChange = async (memberId: string, newRole: string) => {
    await apiClient.patch(`/org-members/${memberId}`, { role: newRole })
    if (organization && orgMember && user) {
      const m = members.find(x => x.id === memberId)
      await auditService.log({
        orgId: organization.id, actorUserId: user.id,
        actorDisplayName: orgMember.display_name || user.email || '',
        action: 'member.role_changed', entityType: 'member',
        entityId: memberId, entityLabel: m?.display_name || '',
        oldValues: { role: m?.role }, newValues: { role: newRole },
      })
    }
    loadMembers()
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Remove this member from the organization?')) return
    const m = members.find(x => x.id === memberId)
    await apiClient.delete(`/org-members/${memberId}`)
    if (organization && orgMember && user) {
      await auditService.log({
        orgId: organization.id, actorUserId: user.id,
        actorDisplayName: orgMember.display_name || user.email || '',
        action: 'member.removed', entityType: 'member',
        entityId: memberId, entityLabel: m?.display_name || '',
      })
    }
    loadMembers()
  }

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organization) return
    setSaving(true); setSaveMsg('')
    try {
      await apiClient.patch(`/organizations/${organization.id}`, {
        name: orgName,
        default_currency: defaultCurrency,
      })
      if (orgMember && user) {
        await auditService.log({
          orgId: organization.id, actorUserId: user.id,
          actorDisplayName: orgMember.display_name || user.email || '',
          action: 'org.settings_updated', entityType: 'organization',
          entityId: organization.id,
          newValues: { name: orgName, default_currency: defaultCurrency },
        })
        setSaveMsg('Settings saved successfully.')
      }
    } catch (err: any) {
      setSaveMsg(err.message || 'Failed to save settings')
    }
    setSaving(false)
  }

  const getLimitDisplay = (current: number, max: number) => {
    if (max === -1) return { label: `${current} / Unlimited`, cls: s.limitValueUsed }
    const pct = current / max
    const cls = pct >= 1 ? s.limitValueOver : pct >= 0.8 ? s.limitValueWarn : s.limitValueUsed
    return { label: `${current} / ${max}`, cls }
  }

  const isAdmin = orgMember?.role === 'admin'
  if (!isAdmin) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#605e5c' }}>
        <h2 style={{ color: '#323130', marginBottom: 8 }}>Access Denied</h2>
        <p>The Admin Panel is only accessible to organization administrators.</p>
        <Link to="/app" style={{ color: '#0078d4', marginTop: 16, display: 'inline-block' }}>Back to app</Link>
      </div>
    )
  }

  const currentTier = subscription?.tier || 'starter'
  const tierLimits = allLimits.find(l => l.tier === currentTier)

  const navItems: { id: AdminSection; label: string; Icon: React.FC<any> }[] = [
    { id: 'overview', label: 'Overview', Icon: BarChart3 },
    { id: 'subscription', label: 'Subscription', Icon: CreditCard },
    { id: 'users', label: 'Users & Roles', Icon: Users },
    { id: 'departments', label: 'Departments', Icon: Building },
    { id: 'settings', label: 'Org Settings', Icon: Settings },
    { id: 'audit', label: 'Audit Log', Icon: ClipboardList },
    { id: 'legal', label: 'Legal & Compliance', Icon: Shield },
    { id: 'observability', label: 'Observability', Icon: Activity },
    { id: 'releases', label: 'Release Notes', Icon: ClipboardList },
  ]

  const filteredAuditLogs = auditLogs.filter(log => {
    const matchesAction = !auditFilter || log.action.includes(auditFilter)
    const matchesEntity = !auditEntityFilter || log.entity_type === auditEntityFilter
    return matchesAction && matchesEntity
  })

  const auditEntityTypes = [...new Set(auditLogs.map(l => l.entity_type))].sort()

  return (
    <div className={s.adminPage}>
      <TourOverlay />
      <header className={s.adminHeader}>
        <div className={s.adminHeaderBrand}>
          <div className={s.adminHeaderBrandIcon}>
            <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" aria-hidden="true">
              <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
          AllocatIQ
          <span style={{ color: '#a19f9d', fontWeight: 400, fontSize: 13 }}>/ {organization?.name}</span>
        </div>
        <div className={s.adminHeaderRight}>
          <span style={{ fontSize: 11, color: '#a19f9d', fontWeight: 500, letterSpacing: '0.3px' }}>v{CURRENT_VERSION}</span>
          <span className={s.adminBadge}>Admin</span>
          <Link to="/app" className={s.backBtn}>← Back to app</Link>
        </div>
      </header>

      <div className={s.adminBody}>
        <aside className={s.adminSidebar}>
          <div className={s.adminNavSection}>
            <div className={s.adminNavLabel}>Admin Panel</div>
            {navItems.map(n => (
              <button
                key={n.id}
                className={`${s.adminNavItem} ${section === n.id ? s.adminNavActive : ''}`}
                onClick={() => setSection(n.id)}
                data-tour-admin={n.id}
              >
                <n.Icon size={15} /> {n.label}
              </button>
            ))}
          </div>
        </aside>

        <main className={s.adminMain}>

          {section === 'overview' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Organization Overview</div>
              <div className={s.adminSectionSub}>{organization?.name} · {PLAN_LABELS[currentTier]} Plan</div>

              {subscription?.status === 'trialing' && daysLeft !== null && (
                <div className={s.trialBanner}>
                  <span className={s.trialText}>
                    Free trial active — {daysLeft} day{daysLeft === 1 ? '' : 's'} remaining
                    <span className={s.trialSub}>Upgrade to keep access after trial ends</span>
                  </span>
                  <button className={s.saveBtn} style={{ marginTop: 0 }} onClick={() => setSection('subscription')}>
                    Upgrade plan →
                  </button>
                </div>
              )}

              <div className={s.card}>
                <div className={s.cardHeader}><h3 className={s.cardTitle}>Plan Limits & Usage</h3></div>
                <div className={s.cardBody}>
                  {[
                    { label: 'Users', current: usage.users, max: tierLimits?.max_users ?? 5 },
                    { label: 'Projects', current: usage.projects, max: tierLimits?.max_projects ?? 10 },
                    { label: 'Resources', current: usage.resources, max: tierLimits?.max_resources ?? 20 },
                  ].map(row => {
                    const { label, cls } = getLimitDisplay(row.current, row.max)
                    return (
                      <div key={row.label} className={s.limitRow}>
                        <span className={s.limitLabel}>{row.label}</span>
                        <span className={cls}>{label}</span>
                      </div>
                    )
                  })}
                  {[
                    { label: 'Gantt Chart', val: tierLimits?.has_gantt },
                    { label: 'Revenue Forecasting', val: tierLimits?.has_revenue_forecast },
                    { label: 'Partner Rate Sheets', val: tierLimits?.has_partner_rates },
                    { label: 'Audit Logging', val: tierLimits?.has_audit_log },
                    { label: 'SSO (Azure/Google)', val: tierLimits?.has_sso },
                  ].map(row => (
                    <div key={row.label} className={s.limitRow}>
                      <span className={s.limitLabel}>{row.label}</span>
                      <span style={{ color: row.val ? '#107c10' : '#a19f9d', fontWeight: 700 }}>
                        {row.val ? '✓ Included' : '✗ Not included'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === 'subscription' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Subscription & Billing</div>
              <div className={s.adminSectionSub}>Manage your plan, payment methods, and billing history via the Stripe portal.</div>

              {subscription?.status === 'trialing' && daysLeft !== null && (
                <div className={s.trialBanner}>
                  <span className={s.trialText}>
                    Trial active<span className={s.trialSub}> — {daysLeft} day{daysLeft === 1 ? '' : 's'} remaining</span>
                  </span>
                  <button className={s.saveBtn} style={{ marginTop: 0 }} onClick={() => setShowTrialModal(true)}>
                    Choose a plan →
                  </button>
                </div>
              )}

              {showTrialModal && (
                <TrialExpirationModal onConverted={() => setShowTrialModal(false)} />
              )}

              <SubscriptionManager />

              <div className={s.card} style={{ marginTop: 32 }}>
                <div className={s.cardHeader}><h3 className={s.cardTitle}>Billing History</h3></div>
                <div className={s.cardBody}>
                  <BillingHistory />
                </div>
              </div>
            </div>
          )}

          <div className={s.adminSection} style={{ display: section === 'users' ? undefined : 'none' }}>
            <div className={s.adminSectionTitle}>Team & Roles</div>
            <div className={s.adminSectionSub}>
              Manage team members, send invitations, and control access levels with granular permissions.
            </div>
            <TeamManagement />
          </div>

          {section === 'departments' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Departments</div>
              <div className={s.adminSectionSub}>
                Organize your team into departments for access control and data segmentation.
              </div>
              <DepartmentManagement />
            </div>
          )}

          {section === 'settings' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Organization Settings</div>
              <div className={s.adminSectionSub}>Update your organization profile and default settings.</div>

              <div className={s.card}>
                <div className={s.cardHeader}><h3 className={s.cardTitle}>General Settings</h3></div>
                <div className={s.cardBody}>
                  <form onSubmit={handleSaveSettings}>
                    {saveMsg && <div className={s.success}>{saveMsg}</div>}
                    <div className={s.formGrid}>
                      <div className={s.formGroup}>
                        <label>Organization Name</label>
                        <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Acme Corp" required />
                      </div>
                      <div className={s.formGroup}>
                        <label>Default Currency</label>
                        <select value={defaultCurrency} onChange={e => setDefaultCurrency(e.target.value as any)}>
                          <option value="USD">USD — US Dollar</option>
                          <option value="GBP">GBP — British Pound</option>
                          <option value="INR">INR — Indian Rupee</option>
                        </select>
                      </div>
                    </div>
                    <button type="submit" className={s.saveBtn} disabled={saving}>
                      {saving ? 'Saving...' : 'Save settings'}
                    </button>
                  </form>
                </div>
              </div>

              <div className={s.card}>
                <div className={s.cardHeader}><h3 className={s.cardTitle}>Organization ID</h3></div>
                <div className={s.cardBody}>
                  <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 8 }}>
                    Use this ID when integrating with external systems or configuring Stripe webhooks.
                  </p>
                  <code style={{ fontSize: 12, background: '#f3f2f1', padding: '6px 10px', borderRadius: 3, display: 'inline-block', color: '#323130' }}>
                    {organization?.id}
                  </code>
                </div>
              </div>
            </div>
          )}

          {section === 'audit' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Audit Log</div>
              <div className={s.adminSectionSub}>Full record of sensitive operations in your organization.</div>

              <div className={s.card}>
                <div className={s.cardHeader} style={{ borderBottom: '1px solid #edebe9', paddingBottom: 12 }}>
                  <h3 className={s.cardTitle}>Events ({filteredAuditLogs.length}{auditLogs.length !== filteredAuditLogs.length ? ` of ${auditLogs.length}` : ''})</h3>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <select
                      value={auditFilter}
                      onChange={e => setAuditFilter(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #c8c6c4', borderRadius: 3, fontSize: 12, color: '#323130', background: '#fff', fontFamily: 'inherit' }}
                    >
                      <option value="">All actions</option>
                      <option value="created">Created</option>
                      <option value="updated">Updated</option>
                      <option value="deleted">Deleted</option>
                      <option value="member">Member changes</option>
                      <option value="org">Org changes</option>
                    </select>
                    <select
                      value={auditEntityFilter}
                      onChange={e => setAuditEntityFilter(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #c8c6c4', borderRadius: 3, fontSize: 12, color: '#323130', background: '#fff', fontFamily: 'inherit' }}
                    >
                      <option value="">All entity types</option>
                      {auditEntityTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {(auditFilter || auditEntityFilter) && (
                      <button
                        onClick={() => { setAuditFilter(''); setAuditEntityFilter('') }}
                        style={{ padding: '6px 12px', border: '1px solid #c8c6c4', borderRadius: 3, fontSize: 12, background: '#fff', cursor: 'pointer', fontFamily: 'inherit', color: '#605e5c' }}
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
                <div className={s.cardBody} style={{ padding: 0 }}>
                  {filteredAuditLogs.length === 0 && (
                    <div className={s.emptyState}>
                      {auditLogs.length === 0
                        ? 'No audit events recorded yet. Actions like creating projects, changing roles, and deleting records will appear here.'
                        : 'No events match the selected filters.'}
                    </div>
                  )}
                  <div style={{ padding: '8px 20px' }}>
                    {filteredAuditLogs.map(log => (
                      <div key={log.id} className={s.auditRow}>
                        <div className={s.auditIcon}>
                          <AuditActionIcon action={log.action} />
                        </div>
                        <div className={s.auditBody}>
                          <div className={s.auditAction}>
                            <strong>{log.actor_display_name || 'System'}</strong>{' '}
                            <span>{log.action.replace('.', ' ')}{log.entity_label ? ` "${log.entity_label}"` : ''}</span>
                          </div>
                          <div className={s.auditMeta}>{log.entity_type} · {log.action}</div>
                        </div>
                        <div className={s.auditTime}>
                          {new Date(log.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {' '}{new Date(log.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {section === 'observability' && (
            <ObservabilityPage />
          )}

          {section === 'legal' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Legal & Compliance</div>
              <div className={s.adminSectionSub}>Track legal document acceptances and compliance status for your organization.</div>

              <div className={s.card}>
                <div className={s.cardHeader}>
                  <h3 className={s.cardTitle}>Legal Documents</h3>
                </div>
                <div className={s.cardBody}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', textDecoration: 'none', fontSize: 14 }}>
                      Terms of Service →
                    </a>
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', textDecoration: 'none', fontSize: 14 }}>
                      Privacy Policy →
                    </a>
                    <a href="/sla" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', textDecoration: 'none', fontSize: 14 }}>
                      Service Level Agreement →
                    </a>
                    <a href="/aup" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', textDecoration: 'none', fontSize: 14 }}>
                      Acceptable Use Policy →
                    </a>
                  </div>
                </div>
              </div>

              <div className={s.card}>
                <div className={s.cardHeader}>
                  <h3 className={s.cardTitle}>Member Acceptance History</h3>
                </div>
                <div className={s.cardBody} style={{ padding: 0 }}>
                  {legalAcceptances.length === 0 ? (
                    <div className={s.emptyState}>
                      No acceptance records found yet. New members will have their acceptances tracked automatically during signup.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #edebe9', background: '#faf9f8' }}>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#605e5c' }}>User</th>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Document</th>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Version</th>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Method</th>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Accepted At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {legalAcceptances.slice(0, 100).map((acceptance) => (
                            <tr key={acceptance.id} style={{ borderBottom: '1px solid #edebe9' }}>
                              <td style={{ padding: '12px 20px', fontSize: 13, color: '#323130' }}>
                                {acceptance.display_name || acceptance.user_email || members.find(m => m.user_id === acceptance.user_id)?.display_name || 'Unknown'}
                              </td>
                              <td style={{ padding: '12px 20px', fontSize: 13, color: '#323130' }}>
                                {getDocumentLabel(acceptance.document_type)}
                              </td>
                              <td style={{ padding: '12px 20px', fontSize: 13, color: '#605e5c', fontFamily: 'monospace' }}>
                                {acceptance.document_version}
                              </td>
                              <td style={{ padding: '12px 20px', fontSize: 13 }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: acceptance.acceptance_method === 'signup' ? '#dff6dd' : '#e1dfdd',
                                  color: acceptance.acceptance_method === 'signup' ? '#107c10' : '#323130'
                                }}>
                                  {acceptance.acceptance_method}
                                </span>
                              </td>
                              <td style={{ padding: '12px 20px', fontSize: 13, color: '#605e5c' }}>
                                {new Date(acceptance.accepted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {' '}
                                {new Date(acceptance.accepted_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {section === 'releases' && (
            <div className={s.adminSection}>
              <div className={s.adminSectionTitle}>Release Notes</div>
              <div className={s.adminSectionSub}>
                Version history and changelog for all updates to AllocatIQ.
              </div>
              <ReleaseNotes />
            </div>
          )}

        </main>
      </div>
    </div>
  )
}

function getDocumentLabel(type: string): string {
  const labels: Record<string, string> = {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    sla: 'Service Level Agreement',
    aup: 'Acceptable Use Policy',
    dpa: 'Data Processing Agreement'
  }
  return labels[type] || type
}
